import { useCallback, useEffect, useMemo } from 'react';
import clamp from 'lodash.clamp';
import { useAppSelector, useDashboardId, useDhId, useLayoutManager, usePanelId } from '@deephaven/dashboard';
import { assertNotNull } from '@deephaven/utils';
import { emitLinkPointSelected, emitLinkSourceDataSelected, emitRegisterLinkTarget } from "./linker/LinkerEvent.js";
import { getColumnSelectionValidatorForDashboard, getLinksForDashboard } from "./redux/index.js";
export function useGridLinker(model, irisGrid) {
  var _useLayoutManager = useLayoutManager(),
    eventHub = _useLayoutManager.eventHub;
  var dashboardId = useDashboardId();
  var dhId = useDhId();
  var panelId = usePanelId();
  var getLinks = useCallback(s => getLinksForDashboard(s, dashboardId), [dashboardId]);
  var links = useAppSelector(getLinks);
  var linkColumns = useMemo(() => {
    var columnSet = new Set();
    links.forEach(link => {
      if (link.start.panelId === dhId) {
        columnSet.add(link.start.columnName);
      }
    });
    return [...columnSet];
  }, [links, dhId]);
  var getColumnSelectionValidator = useCallback(s => getColumnSelectionValidatorForDashboard(s, dashboardId), [dashboardId]);
  var columnSelectionValidator = useAppSelector(getColumnSelectionValidator);
  var isColumnSelectionValid = useCallback(column => {
    if (columnSelectionValidator && column && dhId != null) {
      return columnSelectionValidator(dhId, column, {
        type: 'tableLink'
      });
    }
    return false;
  }, [columnSelectionValidator, dhId]);
  var isSelectingColumn = columnSelectionValidator != null;
  var onDataSelected = useCallback((row, dataMap) => {
    if (dhId == null) {
      return;
    }
    emitLinkSourceDataSelected(eventHub, dhId, dataMap);
  }, [eventHub, dhId]);
  var getCoordinates = useCallback(columnName => {
    var _gridWrapper$getBound, _allColumnXs$get, _allColumnWidths$get;
    if (!model || !irisGrid) {
      return null;
    }
    var gridWrapper = irisGrid.gridWrapper;
    var rect = (_gridWrapper$getBound = gridWrapper === null || gridWrapper === void 0 ? void 0 : gridWrapper.getBoundingClientRect()) !== null && _gridWrapper$getBound !== void 0 ? _gridWrapper$getBound : null;
    if (rect == null || rect.width <= 0 || rect.height <= 0) {
      return null;
    }
    var metrics = irisGrid.state.metrics;
    assertNotNull(metrics);
    var columnHeaderHeight = metrics.columnHeaderHeight,
      allColumnXs = metrics.allColumnXs,
      allColumnWidths = metrics.allColumnWidths,
      right = metrics.right,
      columnHeaderMaxDepth = metrics.columnHeaderMaxDepth;
    var columnIndex = model.getColumnIndexByName(columnName);
    assertNotNull(columnIndex);
    var visibleIndex = irisGrid.getVisibleColumn(columnIndex);
    var columnX = (_allColumnXs$get = allColumnXs.get(visibleIndex)) !== null && _allColumnXs$get !== void 0 ? _allColumnXs$get : 0;
    var columnWidth = (_allColumnWidths$get = allColumnWidths.get(visibleIndex)) !== null && _allColumnWidths$get !== void 0 ? _allColumnWidths$get : 0;
    var x = clamp(visibleIndex > right ? rect.right : rect.left + columnX + columnWidth * 0.5, rect.left, rect.right);
    var y = rect.top + columnHeaderHeight * columnHeaderMaxDepth;
    return [x, y];
  }, [model, irisGrid]);
  var onColumnSelected = useCallback(column => {
    if (dhId == null) {
      return;
    }
    emitLinkPointSelected(eventHub, dhId, column, {
      type: 'tableLink'
    });
  }, [eventHub, dhId]);
  useEffect(function registerTarget() {
    if (!irisGrid || panelId == null || dhId == null) {
      return;
    }
    emitRegisterLinkTarget(eventHub, dhId, {
      getCoordinates,
      setFilterValues: irisGrid.setFilterMap,
      unsetFilterValue: () => {
        // No-op
      },
      panelId
    });
    return () => {
      emitRegisterLinkTarget(eventHub, dhId, null);
    };
  }, [eventHub, dhId, getCoordinates, irisGrid, panelId]);
  return {
    alwaysFetchColumns: linkColumns,
    columnSelectionValidator: isColumnSelectionValid,
    isSelectingColumn,
    onColumnSelected,
    onDataSelected
  };
}
export default useGridLinker;
//# sourceMappingURL=useGridLinker.js.map